/**
 * Swatch Slider - Product Card Collection Component
 * Converts limited swatch display to fully scrollable horizontal slider
 * 
 * Features:
 * - Horizontal scroll with arrow navigation
 * - Click-to-change product card image
 * - Keyboard navigation (Tab, Arrow keys)
 * - Touch swipe support for mobile
 * - Smooth image transitions
 * - SEO-friendly color name exposure
 * 
 * Scope: Collection pages only - does not affect PDP swatches
 */

(function() {
  'use strict';

  // Only run on collection pages
  if (!document.querySelector('.mrk-product-swatches-wrapper-collection')) {
    return;
  }

  const SELECTORS = {
    wrapper: '.mrk-product-swatches-wrapper-collection',
    swatchContainer: '.swatch', // The actual swatch container inside wrapper
    track: '.mrk-collection-swatch-flex',
    swatch: '.swatch-element',
    swatchInput: '.mrk-collection-swatch',
    prevBtn: '.mrk-prev button',
    nextBtn: '.mrk-next-btn button',
    prevContainer: '.mrk-prev',
    nextContainer: '.mrk-next-btn',
    productCard: '.card-product',
    productImage: '.mrk-grid-item-image, .product-img-first, .product-img-second, .card-product__image, .ProductItem__Image, .aspect-ratio-container img, img[data-product-image]',
    productLink: '.card-product, a[href*="/products/"]',
    overflowIndicator: '.swatch-overflow-indicator'
  };

  const CLASSES = {
    active: 'is-active',
    disabled: 'disabled',
    loading: 'card-product__image--loading',
    fadeIn: 'card-product__image--fade-in',
    cardLoading: 'is-loading'
  };

  const SCROLL_AMOUNT = 80; // pixels per arrow click
  const SWATCH_LOADING_STATE = {
    LOADING: 'loading',
    LOADED: 'true',
    EMPTY: 'empty',
    ERROR: 'error'
  };

  function hideSwatchSpinner(wrapper) {
    const spinner = wrapper.querySelector('.Spinner-spinner-RIK');
    if (spinner) {
      spinner.style.display = 'none';
    }
  }

  function resolveSwatchUrl(wrapper) {
    const raw = (wrapper.getAttribute('data-swatch-url') || wrapper.getAttribute('handle') || '').trim();
    if (!raw || raw.indexOf('/products/') === -1) {
      return '';
    }

    try {
      const urlObj = new URL(raw, window.location.origin);
      const pathMatch = urlObj.pathname.match(/\/products\/([^/?#]+)/);
      if (pathMatch && pathMatch[1]) {
        return '/products/' + pathMatch[1] + '?view=swatches';
      }
      urlObj.searchParams.set('view', 'swatches');
      return urlObj.pathname + '?' + urlObj.searchParams.toString();
    } catch (error) {
      if (raw.indexOf('?') > -1) {
        if (!/[?&]view=swatches(?:&|$)/.test(raw)) {
          return raw + '&view=swatches';
        }
        return raw;
      }
      return raw + '?view=swatches';
    }
  }

  function responseHasSwatches(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return !!temp.querySelector('.mrk-collection-swatch-ul');
  }

  function sanitizeSwatchResponse(responseText) {
    return String(responseText || '').replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }

  function loadCollectionSwatches() {
    const wrappers = document.querySelectorAll(SELECTORS.wrapper);
    for (const wrapper of wrappers) {
      const loadedState = wrapper.getAttribute('data-swatch-loaded');
      if (loadedState === SWATCH_LOADING_STATE.LOADED || loadedState === SWATCH_LOADING_STATE.LOADING) {
        continue;
      }

      const swatchUrl = resolveSwatchUrl(wrapper);
      if (!swatchUrl) {
        wrapper.setAttribute('data-swatch-loaded', SWATCH_LOADING_STATE.EMPTY);
        hideSwatchSpinner(wrapper);
        continue;
      }

      wrapper.setAttribute('data-swatch-loaded', SWATCH_LOADING_STATE.LOADING);

      fetch(swatchUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Swatch request failed with HTTP ' + response.status);
          }
          return response.text();
        })
        .then((responseText) => {
          const cleanedResponse = sanitizeSwatchResponse(responseText);
          const target = wrapper.querySelector('.mrk-swatches-inner-collection');
          if (target && responseHasSwatches(cleanedResponse)) {
            target.innerHTML = cleanedResponse;
            wrapper.setAttribute('data-swatch-loaded', SWATCH_LOADING_STATE.LOADED);
            return;
          }
          wrapper.setAttribute('data-swatch-loaded', SWATCH_LOADING_STATE.EMPTY);
        })
        .catch(() => {
          wrapper.setAttribute('data-swatch-loaded', SWATCH_LOADING_STATE.ERROR);
        })
        .finally(() => {
          hideSwatchSpinner(wrapper);
          setTimeout(initSwatchSliders, 100);
        });
    }
  }

  /**
   * Initialize all swatch sliders on the page
   */
  function initSwatchSliders() {
    const wrappers = document.querySelectorAll(SELECTORS.wrapper);
    
    wrappers.forEach(wrapper => {
      // Check if swatches are actually loaded (not just the spinner)
      const swatchesInner = wrapper.querySelector('.mrk-swatches-inner-collection');
      if (!swatchesInner) {
        return;
      }
      
      // Check if swatch container exists
      const swatchContainer = swatchesInner.querySelector(SELECTORS.swatchContainer);
      if (!swatchContainer) {
        return;
      }
      
      if (wrapper.dataset.sliderInitialized) {
        // Re-initialize if swatches were reloaded
        delete wrapper.dataset.sliderInitialized;
      }
      
      wrapper.dataset.sliderInitialized = 'true';
      
      initSlider(wrapper);
    });
  }

  /**
   * Initialize a single slider instance
   * @param {HTMLElement} wrapper - The swatch wrapper element
   */
  function initSlider(wrapper) {
    // Find the actual swatch container (might be nested in swatch-dropdownlist)
    const swatchContainer = wrapper.querySelector(SELECTORS.swatchContainer) || wrapper;
    const track = swatchContainer.querySelector(SELECTORS.track);
    const prevBtn = swatchContainer.querySelector(SELECTORS.prevBtn);
    const nextBtn = swatchContainer.querySelector(SELECTORS.nextBtn);
    const prevContainer = swatchContainer.querySelector(SELECTORS.prevContainer);
    const nextContainer = swatchContainer.querySelector(SELECTORS.nextContainer);
    const swatches = swatchContainer.querySelectorAll(SELECTORS.swatch);
    const productCard = wrapper.closest(SELECTORS.productCard);
    
    if (!track) {
      console.warn('Swatch slider: Track not found', wrapper);
      return;
    }
    
    // Debug logging (remove in production)
    // console.log('Initializing swatch slider:', {
    //   track: !!track,
    //   prevBtn: !!prevBtn,
    //   nextBtn: !!nextBtn,
    //   swatches: swatches.length
    // });

    // Remove overflow indicator
    const overflowIndicator = wrapper.querySelector(SELECTORS.overflowIndicator);
    if (overflowIndicator) {
      overflowIndicator.remove();
    }

    // Show all hidden swatches (remove inline display:none)
    // CSS will handle showing them via !important rules
    swatches.forEach(swatch => {
      // Remove inline styles that might hide swatches
      if (swatch.style.display === 'none') {
        swatch.style.display = '';
        swatch.style.removeProperty('display');
      }
      
      // Remove any classes that might hide it
      swatch.classList.remove('hidden');
      
      // Ensure swatch is visible (CSS !important rules will override nth-child)
      swatch.style.setProperty('display', 'block');
    });

    // Add SEO color count text
    addColorCountText(wrapper, swatches.length);

    // Add ARIA attributes for accessibility
    addAriaAttributes(wrapper, swatches);

    // Navigation arrows - PREVENT BUBBLING TO PRODUCT CARD
    // Only show arrows if there are more than 2 colors
    if (prevBtn && nextBtn && prevContainer && nextContainer) {
      // Hide arrows if 2 or fewer swatches
      if (swatches.length <= 2) {
        prevContainer.style.display = 'none';
        nextContainer.style.display = 'none';
        return; // Exit early, no need to set up arrow handlers
      }
      
      // Show arrows if more than 2 swatches (use !important to override CSS)
      prevContainer.style.setProperty('display', 'flex', 'important');
      nextContainer.style.setProperty('display', 'flex', 'important');
      
      // Remove disabled class initially
      prevContainer.classList.remove('disabled');
      nextContainer.classList.remove('disabled');
      
      // Remove any existing jQuery handlers by using off() if jQuery is available
      if (typeof jQuery !== 'undefined') {
        jQuery(prevBtn).off('click');
        jQuery(nextBtn).off('click');
        jQuery(prevContainer).off('click');
        jQuery(nextContainer).off('click');
        jQuery(prevBtn).off('mousedown');
        jQuery(nextBtn).off('mousedown');
      }
      
      // Click handler - PREVENT BUBBLING
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Don't do anything if disabled
        if (prevContainer.classList.contains('disabled')) {
          return;
        }
        
        scrollTrack(track, -SCROLL_AMOUNT);
      }, true); // Use capture phase to run before jQuery
      
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Don't do anything if disabled
        if (nextContainer.classList.contains('disabled')) {
          return;
        }
        
        scrollTrack(track, SCROLL_AMOUNT);
      }, true); // Use capture phase to run before jQuery
      
      // Also prevent mousedown from bubbling (some themes use this)
      prevBtn.addEventListener('mousedown', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
      
      nextBtn.addEventListener('mousedown', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
      
      // Prevent touch events from bubbling on mobile
      prevBtn.addEventListener('touchstart', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, { passive: true, capture: true });
      
      nextBtn.addEventListener('touchstart', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, { passive: true, capture: true });
      
      // Update arrow visibility on scroll
      track.addEventListener('scroll', debounce(() => {
        updateArrowVisibility(track, prevContainer, nextContainer);
      }, 50), { passive: true });
      
      // Initial arrow state - wait a bit for layout to settle
      setTimeout(() => {
        updateArrowVisibility(track, prevContainer, nextContainer);
      }, 200);
    }

    // Swatch click handlers - image swap
    swatches.forEach(swatch => {
      const input = swatch.querySelector(SELECTORS.swatchInput);
      if (!input) {
        return;
      }

      // Remove existing jQuery handlers if available
      if (typeof jQuery !== 'undefined') {
        jQuery(swatch).off('click');
        jQuery(input).off('change');
        const label = swatch.querySelector('label');
        if (label) {
          jQuery(label).off('click');
        }
      }
      
      // Click handler on the swatch element (including label)
      const label = swatch.querySelector('label');
      if (label) {
        label.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation(); // Prevent jQuery handlers
          input.checked = true;
          handleSwatchClick(swatch, input, swatches, productCard, wrapper);
        }, true); // Use capture phase
      }
      
      // Click handler on the swatch element itself
      swatch.addEventListener('click', (e) => {
        // Don't handle if clicking on label (already handled above)
        if (e.target.closest('label')) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent jQuery handlers
        input.checked = true;
        handleSwatchClick(swatch, input, swatches, productCard, wrapper);
      }, true); // Use capture phase

      // Input change handler (for radio button behavior)
      input.addEventListener('change', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent jQuery handlers
        handleSwatchClick(swatch, input, swatches, productCard, wrapper);
      }, true); // Use capture phase
      
      // Keyboard support
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          input.checked = true;
          handleSwatchClick(swatch, input, swatches, productCard, wrapper);
        }
        
        // Arrow key navigation
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          navigateSwatches(swatch, swatches, e.key === 'ArrowRight' ? 1 : -1, track);
        }
      });
    });

    // Touch swipe support
    let touchStartX = 0;
    let touchEndX = 0;
    
    track.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    track.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe(track, touchStartX, touchEndX);
    }, { passive: true });
  }

  /**
   * Scroll the track by a specified amount
   * @param {HTMLElement} track - The scrollable track
   * @param {number} amount - Pixels to scroll (positive = right, negative = left)
   */
  function scrollTrack(track, amount) {
    track.scrollBy({
      left: amount,
      behavior: 'smooth'
    });
  }

  /**
   * Update arrow visibility based on scroll position
   * @param {HTMLElement} track - The scrollable track
   * @param {HTMLElement} prevContainer - Previous button container
   * @param {HTMLElement} nextContainer - Next button container
   */
  function updateArrowVisibility(track, prevContainer, nextContainer) {
    // Don't update if arrows are hidden (2 or fewer swatches)
    if (prevContainer.style.display === 'none' || nextContainer.style.display === 'none') {
      return;
    }
    
    const isAtStart = track.scrollLeft <= 1;
    const isAtEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    const hasOverflow = track.scrollWidth > track.clientWidth;
    
    // Hide both arrows if no overflow (all swatches visible)
    if (!hasOverflow) {
      prevContainer.classList.add(CLASSES.disabled);
      nextContainer.classList.add(CLASSES.disabled);
      return;
    }
    
    prevContainer.classList.toggle(CLASSES.disabled, isAtStart);
    nextContainer.classList.toggle(CLASSES.disabled, isAtEnd);
    
    const prevBtn = prevContainer.querySelector('button');
    const nextBtn = nextContainer.querySelector('button');
    
    if (prevBtn) prevBtn.setAttribute('aria-disabled', isAtStart);
    if (nextBtn) nextBtn.setAttribute('aria-disabled', isAtEnd);
  }

  /**
   * Handle swatch click - change product card image
   * @param {HTMLElement} swatch - Clicked swatch element
   * @param {HTMLElement} input - The radio input inside the swatch
   * @param {NodeList} allSwatches - All swatches in this slider
   * @param {HTMLElement} productCard - Parent product card
   * @param {HTMLElement} wrapper - The swatch wrapper
   */
  function handleSwatchClick(swatch, input, allSwatches, productCard, wrapper) {
    const variantImage = input.dataset.variantImage;
    const variantUrl = input.getAttribute('url');
    
    if (!productCard) {
      return;
    }

    // Update active state
    allSwatches.forEach(s => {
      s.classList.remove(CLASSES.active);
      const inp = s.querySelector('input');
      if (inp) {
        inp.checked = false;
        inp.removeAttribute('checked');
      }
    });
    swatch.classList.add(CLASSES.active);
    input.checked = true;

    // Update product card image with smooth transition
    if (variantImage) {
      // Try multiple selectors to find the product image
      // First try the specific class used by existing code
      let productImage = productCard.querySelector('.mrk-grid-item-image');
      
      // Fallback to other selectors
      if (!productImage) {
        const productImages = productCard.querySelectorAll(SELECTORS.productImage);
        if (productImages.length > 0) {
          productImage = productImages[0];
        }
      }
      
      if (productImage) {
        changeProductImage(productImage, variantImage, productCard);
      }
    }

    // Update product card link URL to include variant
    if (variantUrl) {
      // Find the main product card link (the card itself might be a link)
      let productLink = productCard;
      if (productCard.tagName !== 'A') {
        productLink = productCard.querySelector('a[href*="/products/"]');
      }
      
      if (productLink && productLink.href) {
        // Build full URL if variantUrl is relative
        const fullVariantUrl = variantUrl.startsWith('http') 
          ? variantUrl 
          : window.location.origin + variantUrl;
        productLink.href = fullVariantUrl;
      }
    }

    // Announce change for screen readers
    const colorName = swatch.dataset.value || input.value;
    announceChange(colorName);
  }

  /**
   * Change product card image with loading state and fade transition
   * @param {HTMLImageElement} imageEl - The image element
   * @param {string} newSrc - New image source URL
   * @param {HTMLElement} productCard - Parent product card
   */
  function changeProductImage(imageEl, newSrc, productCard) {
    if (!imageEl || !newSrc) {
      return;
    }
    
    // Add loading state
    imageEl.classList.add(CLASSES.loading);
    imageEl.classList.remove(CLASSES.fadeIn);
    productCard.classList.add(CLASSES.cardLoading);
    
    // Preload new image
    const preloader = new Image();
    
    preloader.onload = () => {
      // Update src - handle both http and protocol-relative URLs
      let fullSrc = newSrc;
      if (newSrc.startsWith('//')) {
        fullSrc = 'https:' + newSrc;
      } else if (newSrc.startsWith('/')) {
        fullSrc = window.location.origin + newSrc;
      }
      
      
      // Update both src and srcset (existing code uses both)
      imageEl.src = fullSrc;
      imageEl.setAttribute('srcset', fullSrc);
      
      // Also update srcset attribute if present
      if (imageEl.hasAttribute('srcset')) {
        imageEl.srcset = generateSrcset(fullSrc);
      }
      
      // Remove loading, add fade-in
      imageEl.classList.remove(CLASSES.loading);
      imageEl.classList.add(CLASSES.fadeIn);
      productCard.classList.remove(CLASSES.cardLoading);
      
      // Remove animation class after completion
      setTimeout(() => {
        imageEl.classList.remove(CLASSES.fadeIn);
      }, 300);
    };
    
    preloader.onerror = () => {
      imageEl.classList.remove(CLASSES.loading);
      productCard.classList.remove(CLASSES.cardLoading);
    };
    
    // Handle protocol-relative URLs
    let preloadSrc = newSrc;
    if (newSrc.startsWith('//')) {
      preloadSrc = 'https:' + newSrc;
    } else if (newSrc.startsWith('/')) {
      preloadSrc = window.location.origin + newSrc;
    }
    preloader.src = preloadSrc;
  }

  /**
   * Generate srcset for responsive images
   * @param {string} src - Base image source
   * @returns {string} - Srcset attribute value
   */
  function generateSrcset(src) {
    // Extract base URL without width parameter
    const baseUrl = src.replace(/&width=\d+/, '').replace(/\?width=\d+/, '');
    const connector = baseUrl.includes('?') ? '&' : '?';
    
    return [
      `${baseUrl}${connector}width=200 200w`,
      `${baseUrl}${connector}width=400 400w`,
      `${baseUrl}${connector}width=600 600w`
    ].join(', ');
  }

  /**
   * Navigate between swatches with keyboard
   * @param {HTMLElement} currentSwatch - Currently focused swatch
   * @param {NodeList} allSwatches - All swatches
   * @param {number} direction - 1 for next, -1 for previous
   * @param {HTMLElement} track - The scrollable track
   */
  function navigateSwatches(currentSwatch, allSwatches, direction, track) {
    const swatchArray = Array.from(allSwatches);
    const currentIndex = swatchArray.indexOf(currentSwatch);
    const newIndex = Math.max(0, Math.min(swatchArray.length - 1, currentIndex + direction));
    
    if (swatchArray[newIndex]) {
      const newSwatch = swatchArray[newIndex];
      const input = newSwatch.querySelector('input');
      
      if (input) {
        input.focus();
        
        // Scroll swatch into view if needed
        const swatchRect = newSwatch.getBoundingClientRect();
        const trackRect = track.getBoundingClientRect();
        
        if (swatchRect.left < trackRect.left) {
          scrollTrack(track, swatchRect.left - trackRect.left - 10);
        } else if (swatchRect.right > trackRect.right) {
          scrollTrack(track, swatchRect.right - trackRect.right + 10);
        }
      }
    }
  }

  /**
   * Handle touch swipe gestures
   * @param {HTMLElement} track - The scrollable track
   * @param {number} startX - Touch start X position
   * @param {number} endX - Touch end X position
   */
  function handleSwipe(track, startX, endX) {
    const threshold = 30; // minimum swipe distance
    const diff = startX - endX;
    
    if (Math.abs(diff) > threshold) {
      scrollTrack(track, diff > 0 ? SCROLL_AMOUNT : -SCROLL_AMOUNT);
    }
  }

  /**
   * Add SEO-friendly color count text
   * @param {HTMLElement} wrapper - The swatch wrapper
   * @param {number} count - Number of colors
   */
  function addColorCountText(wrapper, count) {
    // Check if already added
    if (wrapper.querySelector('.swatch-slider__color-count')) return;
    if (count <= 1) return; // Don't show for single color
    
    const countEl = document.createElement('div');
    countEl.className = 'swatch-slider__color-count';
    countEl.setAttribute('aria-hidden', 'true');
    countEl.textContent = count + ' colour' + (count !== 1 ? 's' : '') + ' available';
    
    // Insert after the swatch container (try multiple locations)
    const swatchContainer = wrapper.querySelector('.swatch') || wrapper.querySelector('.swatch-dropdownlist');
    if (swatchContainer) {
      swatchContainer.after(countEl);
    } else {
      // Fallback: append to wrapper
      wrapper.appendChild(countEl);
    }
  }

  /**
   * Add ARIA attributes for accessibility
   * @param {HTMLElement} wrapper - The swatch wrapper
   * @param {NodeList} swatches - All swatch elements
   */
  function addAriaAttributes(wrapper, swatches) {
    // Find track - might be nested
    const swatchContainer = wrapper.querySelector(SELECTORS.swatchContainer) || wrapper;
    const track = swatchContainer.querySelector(SELECTORS.track);
    
    if (track) {
      track.setAttribute('role', 'listbox');
      track.setAttribute('aria-label', 'Select colour');
    }
    
    // Add option role to each swatch
    swatches.forEach((swatch, index) => {
      swatch.setAttribute('role', 'option');
      const colorName = swatch.dataset.value;
      const isSoldout = swatch.classList.contains('soldout');
      swatch.setAttribute('aria-label', colorName + (isSoldout ? ' - Sold out' : ''));
      swatch.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    });
  }

  /**
   * Announce changes for screen readers
   * @param {string} colorName - Name of selected color
   */
  function announceChange(colorName) {
    // Use existing live region or create one
    let liveRegion = document.getElementById('swatch-live-region');
    
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'swatch-live-region';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only visually-hidden';
      liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;';
      document.body.appendChild(liveRegion);
    }
    
    liveRegion.textContent = 'Selected colour: ' + colorName;
  }

  /**
   * Debounce utility function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Milliseconds to wait
   * @returns {Function} - Debounced function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * Hook into initSwatches function to initialize after swatches load
   */
  function hookIntoInitSwatches() {
    if (typeof window.initSwatches === 'function') {
      const originalInitSwatches = window.initSwatches;
      window.initSwatches = function() {
        const result = originalInitSwatches.apply(this, arguments);
        // Initialize sliders after swatches are loaded
        setTimeout(() => {
          initSwatchSliders();
        }, 200);
        return result;
      };
      return;
    }

    // Fallback for collection pages where legacy mrk.js is not loaded.
    window.initSwatches = loadCollectionSwatches;
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      hookIntoInitSwatches();
      if (typeof window.initSwatches === 'function') {
        window.initSwatches();
      }
      // Initial run after a delay to catch already-loaded swatches
      setTimeout(initSwatchSliders, 500);
    });
  } else {
    hookIntoInitSwatches();
    if (typeof window.initSwatches === 'function') {
      window.initSwatches();
    }
    // Initial run after a delay to catch already-loaded swatches
    setTimeout(initSwatchSliders, 500);
  }

  // Re-initialize after Shopify section loads (for theme editor)
  document.addEventListener('shopify:section:load', () => {
    setTimeout(initSwatchSliders, 300);
  });
  
  // Re-initialize after AJAX pagination/filtering (collection pages)
  document.addEventListener('collection:updated', () => {
    setTimeout(initSwatchSliders, 300);
  });
  document.addEventListener('collection:pagechange', () => {
    setTimeout(initSwatchSliders, 300);
  });
  
  // Observe DOM for dynamically added product cards (Load More, infinite scroll)
  const observer = new MutationObserver((mutations) => {
    let shouldInit = false;
    
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if swatches were added
            if (node.matches && (
              node.matches(SELECTORS.wrapper) || 
              node.matches(SELECTORS.swatchContainer) ||
              node.matches('.swatch-dropdownlist') ||
              (node.querySelector && (
                node.querySelector(SELECTORS.wrapper) || 
                node.querySelector(SELECTORS.swatchContainer) ||
                node.querySelector('.swatch-dropdownlist')
              ))
            )) {
              shouldInit = true;
            }
          }
        });
      }
    });
    
    if (shouldInit) {
      // Debounce to avoid multiple rapid initializations
      setTimeout(initSwatchSliders, 200);
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true });
  
  // Also listen for jQuery AJAX complete (swatches load via AJAX)
  if (typeof jQuery !== 'undefined') {
    jQuery(document).ajaxComplete(function() {
      setTimeout(initSwatchSliders, 300);
    });
  }

})();
